import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import * as AudioManager from '../../utils/AudioManager';
import * as emitterManager from '../../utils/EmitterManager';
import * as PhysicsParticleEmitter from '../../utils/PhysicsParticleEmitter';
import { cutRing } from './laserTracking';
import { cloneHalf, setGroupIndex, slideOff, swapHalfTextures } from './laserHalves';
import { burstRing, startDrip } from './laserGore';

const CUT_FACES = {
	WaterMelon: {flesh: 0xf2293a, seeds: 0x2b1b14, count: 9},
	Tomato: {flesh: 0xe8261c, seeds: 0xf2d05f, count: 7},
	Orange: {flesh: 0xff9a2e},
	Pear: {flesh: 0xf3eec2, seeds: 0x5b3a1e, count: 3},
	Banana: {flesh: 0xf6ebc0},
	Pumpkin: {flesh: 0xffa21f, seeds: 0xf4e2b1, count: 8},
	CarvedPumpkin: {flesh: 0xffa21f, seeds: 0xf4e2b1, count: 8},
	Turkey: {flesh: 0xf1d2b8},
};

const bakedFaces = new Map();

// A flat-coloured inside with the art's own edge as the peel, until the props get real cut art.
const fruitCutTexture = prefabClass => {
	const name = prefabClass.base.myTexture.data.textureName;
	if (bakedFaces.has(name)) return bakedFaces.get(name);
	const style = CUT_FACES[prefabClass.name] || {};
	const flesh = style.flesh ?? parseInt(prefabClass.splashColors[0].slice(1), 16);
	const source = PIXI.Texture.from(name);
	const width = source.width;
	const height = source.height;
	const container = new PIXI.Container();
	const peel = new PIXI.Sprite(source);
	const inside = new PIXI.Sprite(source);
	inside.anchor.set(0.5);
	inside.position.set(width / 2, height / 2);
	inside.scale.set(0.8);
	const fill = new PIXI.ColorMatrixFilter();
	fill.matrix = [
		0, 0, 0, 0, ((flesh >> 16) & 255) / 255,
		0, 0, 0, 0, ((flesh >> 8) & 255) / 255,
		0, 0, 0, 0, (flesh & 255) / 255,
		0, 0, 0, 1, 0,
	];
	inside.filters = [fill];
	container.addChild(peel, inside);
	if (style.seeds !== undefined) {
		const seeds = new PIXI.Graphics();
		for (let i = 0; i < style.count; i++) {
			const angle = i * 2.39996;
			const distance = Math.sqrt((i + 0.5) / style.count) * 0.24;
			seeds.ellipse(width / 2 + Math.cos(angle) * width * distance, height / 2 + Math.sin(angle) * height * distance, 1.6, 2.4);
		}
		seeds.fill({color: style.seeds});
		container.addChild(seeds);
	}
	const texture = game.app.renderer.generateTexture({target: container, frame: new PIXI.Rectangle(0, 0, width, height)});
	container.destroy({children: true});
	bakedFaces.set(name, texture);
	return texture;
};

// The skin half slides off and the back half shows the inside.
export const splitFruit = (prefabClass, back) => {
	back.depthCut = 'back';
	const position = back.GetPosition();
	emitterManager.playOnceEmitter('splash', null, position, 0, prefabClass.splashColors);
	AudioManager.playSFX('laser_slice', 0.5, 1 + Math.random() * 0.2, position);
	AudioManager.playSFX(prefabClass.sound, 0.2, 0.8 + 0.4 * Math.random(), position);
	if (prefabClass.prefabObject?.settings?.hasDebris) {
		const parts = prefabClass.partsRandom
			? Array.from({length: prefabClass.partsType[1]}, (_, i) => `${prefabClass.partsType[0]}_${i + 1}`)
			: prefabClass.partsType;
		PhysicsParticleEmitter.emitDebris(parts, position, 3, prefabClass.partsSize, prefabClass.partsForce * 0.5, prefabClass.partsRandom, prefabClass.partsColors, back.GetAngle(), prefabClass.partsOffset);
	}
	const front = cloneHalf(back, false);
	front.depthCut = 'front';
	const group = game.editor.uniqueCollisions--;
	setGroupIndex(back, group);
	setGroupIndex(front, group);
	swapHalfTextures(back, front, fruitCutTexture(prefabClass));
	slideOff(front, back);
	const ring = cutRing(back.depthCutState.shapes);
	for (const half of [back, front]) {
		burstRing('juiceRing', half, ring, prefabClass.splashColors);
		startDrip('juiceDrip', half, ring, prefabClass.splashColors);
	}
};
